import { useCallback } from 'react'

import { InvoiceDocumentType } from '@features/invoices/enums/invoiceDocumentType'
import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import { getPaymentMethodsTrackingOptions } from '@features/invoices/utils/getPaymentMethodsTrackingOptions'

import { ApproveInvoiceDraftOptions, useApproveInvoiceDraft } from './useApproveInvoiceDraft'
import { useCreateInvoiceDraft } from './useCreateInvoiceDraft'
import { useInvoiceDraftPayload } from './useInvoiceDraftPayload'
import { useInvoiceEditorDocumentType } from './useInvoiceEditorDocumentType'
import { useSaveInvoiceDraft } from './useSaveInvoiceDraft'

interface SaveAndApproveInvoiceOptions extends Omit<ApproveInvoiceDraftOptions, 'paymentMethodsTrackingOptions'> {
  formData: InvoiceForm
}

export const useSaveAndApproveInvoice = () => {
  const { getInvoiceDraftPayload, isLoading: isLoadingDraftPayload } = useInvoiceDraftPayload()
  const { createInvoiceDraft, isProcessing: isProcessingCreateInvoiceDraft } = useCreateInvoiceDraft()
  const {
    saveInvoiceDraft,
    isLoading: isLoadingSaveInvoiceDraft,
    isProcessing: isProcessingSaveInvoiceDraft,
  } = useSaveInvoiceDraft()
  const { approveInvoiceDraft, isProcessing: isProcessingApproveInvoiceDraft } = useApproveInvoiceDraft()
  const { documentType } = useInvoiceEditorDocumentType()

  const isLoading = isLoadingDraftPayload || isLoadingSaveInvoiceDraft
  const isProcessing = isProcessingApproveInvoiceDraft || isProcessingSaveInvoiceDraft || isProcessingCreateInvoiceDraft

  const handleInvoice = useCallback(
    ({ formData, onError, onSuccess }: SaveAndApproveInvoiceOptions) => {
      const { paymentMeans } = getInvoiceDraftPayload(documentType, formData)

      saveInvoiceDraft({
        formData,
        onError,
        onSuccess: (savedDocumentId) => {
          approveInvoiceDraft(savedDocumentId, documentType, {
            onError,
            onSuccess,
            paymentMethodsTrackingOptions: getPaymentMethodsTrackingOptions(paymentMeans),
          })
        },
      })
    },
    [approveInvoiceDraft, documentType, getInvoiceDraftPayload, saveInvoiceDraft],
  )
  const handleCreditNote = useCallback(
    ({ formData, onError, onSuccess }: SaveAndApproveInvoiceOptions) => {
      const invoiceDraftPayload = getInvoiceDraftPayload(documentType, formData)

      createInvoiceDraft(
        { payload: invoiceDraftPayload, documentType },
        {
          onError,
          onSuccess: (draftSaveResponse) => {
            const draftId = draftSaveResponse?.data?.id
            approveInvoiceDraft(draftId, documentType, {
              onError,
              onSuccess,
            })
          },
        },
      )
    },
    [approveInvoiceDraft, createInvoiceDraft, documentType, getInvoiceDraftPayload],
  )

  const saveAndApproveInvoice = useCallback(
    ({ formData, onError, onSuccess }: SaveAndApproveInvoiceOptions) => {
      if (documentType === InvoiceDocumentType.CreditNote) {
        return handleCreditNote({ formData, onError, onSuccess })
      }

      return handleInvoice({ formData, onError, onSuccess })
    },
    [documentType, handleCreditNote, handleInvoice],
  )

  return { saveAndApproveInvoice, isProcessing, isLoading }
}
